import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n, catLabel } from "../lib/i18n";
import { StorefrontHeader } from "../components/StorefrontHeader";
import { PromoBanners } from "../components/PromoBanners";
import { OnlineCart } from "../components/OnlineCart";
import { useReorder } from "../lib/useReorder";
import { useStore } from "../lib/store";
import { useCart } from "../lib/cart";
import { CategoryGrid } from "../components/CategoryGrid";
import { ProductRow } from "../components/ProductRow";
import { ProductCard } from "../components/ProductCard";
import { FlashSale } from "../components/FlashSale";
import { WhatsAppFloat } from "../components/WhatsAppFloat";
import { PosterSlot } from "../components/PosterSlot";
import { RestoNotice } from "../components/RestoNotice";
import { jstToday } from "../lib/jst";
import { ProductSearch, matchesQuery } from "../components/ProductSearch";
import { uniqueCats, sameCat } from "../lib/cats";

export default function RestoOnline() {
  const { t, lang } = useI18n();
  const { categoryOrder, categoryIcons, featuredIds } = useStore();
  const [menus, setMenus] = useState([]);
  const [q, setQ] = useState("");
  const [items, setItems] = useCart("resto");
  const featuredMenus = useMemo(
    () => featuredIds.map((id) => menus.find((m) => m.id === id)).filter(Boolean),
    [featuredIds, menus]
  );
  const newestMenus = useMemo(
    () =>
      [...menus]
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
        .slice(0, 10),
    [menus]
  );
  const addedRef = useRef(null);
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "__all__";
  const setCat = (c) => setParams(c === "__all__" ? {} : { cat: c });

  useEffect(() => {
    api.get("/products", { params: { kind: "menu" } }).then(({ data }) => setMenus(data));
  }, []);
  useReorder("resto", menus, setItems);

  useEffect(() => {
    const addId = params.get("add");
    if (!addId || !menus.length) return;
    if (addedRef.current === addId) return;
    addedRef.current = addId;
    const p = menus.find((x) => x.id === addId);
    if (p) {
      setItems((prev) =>
        prev.find((i) => i.product_id === p.id)
          ? prev.map((i) => (i.product_id === p.id ? { ...i, qty: i.qty + 1 } : i))
          : [...prev, { product_id: p.id, name: p.name, price: p.price, qty: 1, unit: p.unit, category: p.category }]
      );
      toast.success(`${p.name} ${t("added_to_cart")}`);
    }
    setParams({}, { replace: true });
  }, [params, menus, setParams, setItems, t]);

  const cats = ["__all__", ...uniqueCats(menus.map((m) => m.category))];
  const todayStr = jstToday();
  const hasToday = menus.some((m) => m.today_date === todayStr);
  const list = menus.filter(
    (m) =>
      (cat === "__all__" ||
        (cat === "__today__" ? m.today_date === todayStr : sameCat(m.category, cat))) &&
      matchesQuery(m, q)
  );
  const results = list;
  const onSearch = (v) => {
    setQ(v);
    if (v.trim().length === 1)
      document.getElementById("catalog-section")?.scrollIntoView({ behavior: "smooth" });
  };

  const pick = (p) =>
    setItems((prev) => {
      const f = prev.find((i) => i.product_id === p.id);
      if (f)
        return prev.map((i) => (i.product_id === p.id ? { ...i, qty: i.qty + 1 } : i));
      return [
        ...prev,
        { product_id: p.id, name: p.name, price: p.price, qty: 1, unit: p.unit, category: p.category },
      ];
    });

  return (
    <div className="min-h-screen bg-background overflow-x-clip">
      <StorefrontHeader active="resto" />
      <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8 grid lg:grid-cols-3 xl:grid-cols-4 gap-8 min-w-0">
        <div className="lg:col-span-2 xl:col-span-3 space-y-6 min-w-0">
          <PromoBanners channel="resto" />
          <ProductSearch value={q} onChange={onSearch} count={results.length} prefix="resto" />
          <FlashSale />
          <div>
            <h1 className="font-display text-3xl lg:text-4xl font-semibold">
              {t("resto_title")}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{t("resto_sub")}</p>
          </div>
          <RestoNotice />
          {!q.trim() && (
            <>
              <CategoryGrid
                products={menus}
                order={categoryOrder}
                icons={categoryIcons}
                cat={cat}
                setCat={setCat}
                prefix="resto"
              />
              <ProductRow
                title={t("featured_products")}
                products={featuredMenus}
                onPick={pick}
                prefix="resto-featured"
              />
              <ProductRow
                title={t("new_arrivals")}
                products={newestMenus}
                onPick={pick}
                prefix="resto-new"
              />
            </>
          )}
          <PosterSlot slot="catalog_mid" channel="resto" />
          <div
            className="flex gap-2 overflow-x-auto pb-1 scroll-mt-4"
            id="catalog-section"
            data-testid="resto-catalog-section"
          >
            {hasToday && (
              <button
                data-testid="resto-category-today"
                onClick={() => setCat("__today__")}
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors duration-200 ${
                  cat === "__today__"
                    ? "bg-amber-500 text-white"
                    : "bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100"
                }`}
              >
                {t("menu_today")}
              </button>
            )}
            {cats.map((c) => (
              <button
                key={c}
                data-testid={`resto-category-${c === "__all__" ? "all" : c}`}
                onClick={() => setCat(c)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                  cat === c
                    ? "bg-gray-900 text-white"
                    : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
              >
                {c === "__all__" ? t("all") : catLabel(c, lang)}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-4">
            {list.length === 0 && (
              <p className="col-span-full text-sm text-muted-foreground py-6" data-testid="resto-empty">
                {t("no_data")}
              </p>
            )}
            {list.map((m) => (
              <ProductCard
                key={m.id}
                product={m}
                onPick={pick}
                testid={`resto-product-${m.id}`}
              />
            ))}
          </div>
          <PosterSlot slot="footer" channel="resto" />
        </div>
        <div className="lg:sticky lg:top-6 h-fit space-y-6">
          <OnlineCart channel="resto" items={items} setItems={setItems} />
          <PosterSlot slot="sidebar" channel="resto" />
        </div>
      </div>
      <WhatsAppFloat />
    </div>
  );
}
